import { TaskContent, TaskId } from "@/types/content-types";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { TimelineData, TimelineTaskDialog } from "@/components//TimelineTask";
import { ReadDialog } from "@/components//ReadDialog";
import { LifeMapData, LifeMapDialog } from "@/components/ChapterCard/LifeMap";
import { Dialog } from "@/components/Dialog";
import { TaskContentElement } from "./TaskContent";
import { Values } from "./Types";
import { TaskForm } from "./Task";
import { useTask } from "@/contexts/appContent/appContentHooks";
import { CloudStatusIcon } from "../CloudStatusIcon";

const textStep1a = `
## *Steg 1 av 9 Blicka tillbaka med medkänsla*
 Den här resan börjar med att se tillbaka. Inte för att fastna där, utan för att 
lära. Din historia har format den du är i dag. Den rymmer ledtrådar, mönster, 
vändpunkter och visdom som, när de ses med medkänsla, blir kraftfulla väg
visare för vägen framåt.

Att reflektera över det förflutna hjälper dig att förstå din livsresa och vilken 
riktning ditt nuvarande liv tagit. Utmaningen i att blicka bakåt ligger inte i 
att inte förneka det som hänt eller att försköna det med alltför positiva förklaringar. Livet är som det är och det förflutna är alltid undflyende, redan utom 
räckhåll. Att reflektera handlar inte om att döma, utan om att lära – med 
medkänsla, kärlek och en nypa humor. Vi är trots allt människor, och det är 
världen omkring oss också.

 När vi ser tillbaka med öppet hjärta handlar det inte om att förstå allt till 
perfektion, utan om att omfamna de lärdomar, den utveckling och till och 
med de snedsteg som livet har erbjudit. Genom att hitta humor i våra misstag 
och vänlighet i våra utmaningar kan vi förvandla erfarenheterna till en språngbräda 
mot en ljusare och mer meningsfull framtid. Att reflektera över ditt förflutna 
på ett sätt som stödjer din livsresa kan vara djupt stärkande. Målet är som sagt 
inte att fastna i det som hänt, utan att hämta visdom ur det så att du kan gå 
vidare med klarhet, syfte och självmedkänsla.
 Så här kan du kan reflektera över det förflutna på ett enkelt och kärleksfullt 
sätt, fritt från skuld och skam.

## *Skapa förutsättningar för reflektion*
 Avsätt tid i stillhet och närvaro. Oavsett om du skriver, promenerar eller bara 
sitter med dina tankar, ge dig själv utrymme att i lugn och ro möta dina tidigare erfarenheter utan distraktion eller dömande. En fridfull miljö hjälper 
reflektionen att få ta form naturligt och utan press.

 När du reflekterar, möt dina tankar med varsamma och vägledande frågor. 
Använd gärna en livstidslinje från då till nu för att få perspektiv. Det hjälper 
dig att se hur olika faser format den du är i dag. I stället för att fokusera på vad 
som var rätt eller fel, ställ öppna frågor som bjuder in nyfikenhet och vänlighet.
 Se på de stunder, situationer och insikter som fick dig att förändras, att 
öppna eller stänga dig, att välja om, att växa. Fundera över vad dessa erfarenheter ledde till och varför. Reflektera över när och varför du tog vissa beslut 
eller när du bara lät saker ske.

 Föreställ dig ditt liv som en tvättlina, där varje klädnypa håller ett betydelsefullt ögonblick som har format dig. Dagen du valde att tala sanning i en svår 
situation. Ögonblicket då du vågade bjuda ut någon. Förlusten av en älskad. 
Ett nytt jobb, en skilsmässa, ett giftermål. Dagen du sa ja till livet. När du 
bröt en skadlig vana eller fann styrkan att förlåta. Det här är milstolparna på 
din resa. Tillåt dig att känna dem alla, de ljusa, de smärtsamma och de vackert 
röriga, utan att döma.

 Inkludera både din inre värld med känslor, tankar och subtila förnimmelser 
och din yttre värld med situationer, relationer och livsomständigheter när du 
skapar din personliga tvättlina av avgörande ögonblick. Varje nypa på linan 
berättar en del av din historia, formad inte bara av vad som hände, utan av hur 
det rörde sig genom dig.

 När du är klar med din tvättlina, ta en stund att slappna av, andas och 
reflektera. Skynda dig inte genom övningen, det här handlar inte om att göra, utan om att upptäcka. Om du märker att du glider in i prestationsläge, pausa. 
Börja om, mjukt. Detta är din möjlighet att möta en del av dig själv från en 
avslappnad, nyfiken och öppen plats.
`;

const textStep1b = `
## *Fira utveckling och framgångar*
 Fokusera inte bara på de svåra stunderna. Ta dig tid att fira de utmaningar du 
har övervunnit, de meningsfulla prestationer du har uppnått och den glädje 
du har upplevt. Att reflektera över dina personliga segrar, oavsett hur små de 
är, stärker ditt självförtroende och påminner dig om din inre styrka.

 ## *Använd ditt förflutna som en språngbräda in i verkligheten*
 När du har reflekterat, omvandla dina insikter till handling. Låt dina erfarenheter från det förflutna bli en språngbräda som driver din personliga utveckling 
och skapar ditt liv framåt med en djupare förståelse och kunskap om 
dig själv.

## *Öva självmedkänsla*
 Reflektion handlar inte om självkritik, det handlar om att hedra den resa som 
har format dig. När du närmar dig den med vänlighet och erkänner att varje 
erfarenhet, både glädjefylld och utmanande, har bidragit till din utveckling, 
omvandlar du ditt förflutna till en källa till visdom som vägleder dig att skapa 
en meningsfull och självsäker framtid.
`;

const questionsStep1b: TaskContent = [{
    title: "*Här är några vägledande frågor som kan stötta dig i processen*",
    body: [`När du utforskar frågorna nedan, var uppmärksam på tonen i hur du ställer 
dem till dig själv. Möter du dig med nyfikenhet eller finns det ett lager av skam 
eller undvikande? Om det senare är fallet, pausa, andas och mjukna. Våga gå 
lite djupare. Som en diamant är det ofta det varsamma, stadiga trycket som 
lockar fram den verkliga glansen.`,
        `Vilka ögonblick, stora eller små, har format mig mest och vad ledde de till?`,
    ],
    hasTextBox: true,
},
{
    body: ` Vilka erfarenheter gav mig glädje, och vad avslöjar de om vad som 
verkligen betyder något för mig?`,
    hasTextBox: true,
},
{
    body: ` Vad har jag tenderat att undvika och hur har utmaningar hjälpt mig 
att växa trots det?`,
    hasTextBox: true,
},
{
    body: `När jag ser tillbaka, vilka mönster eller lärdomar återkommer gång på 
gång i min livshistoria?`,
    "hasTextBox": true
},
{
    body: `Dessa frågor är utformade för att avslöja mening utan att leda till ånger eller 
överanalysering. 

Genom att reflektera på detta sätt bygger du en grund av förståelse och 
acceptans, vilket omvandlar ditt förflutna till en källa av styrka och vägledning 
för din framtid.

 Alla har vi stunder i vårt förflutna som vi kanske önskar hade blivit annorlunda, men att reflektera med fokus på lärdomar i stället för ånger hjälper oss att 
skifta vårt perspektiv. 

I stället för att fråga *Vad kunde jag ha gjort annorlunda? kan du fråga Hur 
skulle jag göra det annorlunda i dag?* eller *Vad lärde jag mig och hur kan jag 
använda den erfarenheten nu?* På så sätt förvandlas det förflutna till en källa av 
styrka och visdom.`
},
{
    title: "*Identifiera mönster och teman*",
    body: `När du reflekterar, var uppmärksam på mönster i ditt förflutna, återkommande teman, beteenden eller situationer. Dessa mönster kan ge viktiga insikter 
om dina värderingar, önskningar och val. De kan också hjälpa dig att se vilka 
områden du vill förändra framåt.

 Om du till exempel märker ett mönster av att undvika vissa möjligheter på 
grund av rädsla, kan du medvetet välja att vara modigare i framtiden. Eller om 
du ser att meningsfulla relationer har varit en källa till glädje, kan du välja att 
prioritera dem på din fortsatta resa.`
},
{
    title: "*Fråga dig själv*",
    body: [
        "Hur kan jag använda lärdomarna från mitt förflutna för att vägleda mina val i dag?",
        "Vilka värderingar har mina erfarenheter avslöjat, och hur formar de vägen framåt?",
        "Hur kan jag bryta mig fri från ohjälpsamma mönster och skapa nya som stödjer min utveckling?",

        "Vad bör jag vara särskilt uppmärksam på framöver?",
        "Låt inte ditt förflutna definiera dig. Låt det i stället informera dig, stödja dig och hjälpa dig att gå framåt med större visdom och avsikt.",
    ],
    "hasTextBox": true
}];

const textStep2 = `
 ## *Steg 2 av 9 Definiera dina fem nyckelområden*
 Tänk att få hålla i en karta över ditt liv. En karta du själv ritat som hjälper dig 
att förstå vad som är viktigt, var du befinner dig just nu och hur du kan ta dig 
vidare med medvetenhet och riktning.

 Det här är precis vad din livskarta är tänkt att vara. Ett levande dokument, 
skapat av dig själv, för att hjälpa dig navigera i de delar av livet som har störst 
betydelse. Genom att förstå och ta ansvar för några centrala livsområden, så
dana som påverkar hur du mår, utvecklas och relaterar till livet, får du ett 
kraftfullt verktyg. Det handlar inte om att kontrollera livet, utan om att möta 
det med beredskap, flexibilitet och ägarskap. När vi har en överblick över våra 
viktigaste områden kan vi lättare se vad som händer när något skaver. 
## Om du till exempel ...
 ### går igenom en sjukdomsperiod påverkar det främst området jag,  men det kan också spilla över på andra områden, som familj eller arbete. Eller om du till exempel förlorar ett jobb, så är det tydligt att det påverkar din sysselsättning, men det kan också väcka frågor kring identitet, självvärde eller dina relationer.
 
 I stället för att försöka fixa allt på en gång, vilket ofta leder till utmattning eller 
förvirring, kan du genom din karta över livet tydligt urskilja exakt till vilket 
område din energi och uppmärksamhet behöver riktas. Du tränar dig i att 
sortera, prioritera och förstå hur allt hänger ihop utan att tappa fotfästet när 
livet svänger. 
## *Att leva med eller utan en livskarta skiljer sig åt*
 Tänk dig två personer som båda går igenom en oväntad livsförändring, som 
att behöva säga upp sig från sitt jobb eller att drabbas av sjukdom. Den ena 
har skapat en livskarta och har tydlig kontakt med sina värderingar och 
livsområden. 

Personen stannar upp, tittar på helheten och ser att det nu är tid att fokusera på områdena *Sysselsättning eller Jag*. Personen har lätt att sortera bland 
sina känslor, söka stöd i sin gemenskap och fatta beslut som är i linje med den 
riktning hen vill leva i.

 Den andra personen har inte gjort detta arbete. Hos hen uppstår i stället 
diffus oro, blandade känslor och ett försök att hantera allt samtidigt. Tankarna 
snurrar, självtvivlet ökar och personen har svårt att veta var hen ens ska börja. 
I brist på struktur fastnar personen i reaktiva beslut eller undvikanden, 
vilket gör det ännu svårare att återfå fotfästet eller att ta modiga steg mot det 
som kallar. Det blir också lätt att personen gör det mer komplicerat än det är 
att våga luta sig in i livet, oavsett var hen befinner sig, vad som händer eller 
vad hen väljer.

 Men livskartan är inte bara ett stöd när det stormar, den är också ett kraftfullt verktyg när vi vill förverkliga det möjliga. När vi bär på en längtan, dröm 
eller idé behöver vi vara i kontakt med verkligheten för att förstå hur våra livsområden samspelar och vad som krävs för att ta ett steg framåt. Med en 
tydlig inre karta blir det lättare att mobilisera mod, kraft och riktning. Vi ser 
inte bara hinder, utan också möjligheter.

 Skillnaden ligger inte i att slippa livets svängningar, utan i hur vi möter 
dem. En livskarta gör inte livet perfekt, men den ger dig något att hålla i när 
det stormar och en karta när du vill göra det möjliga möjligt.
 I detta steg börjar du skapa grunden till din livskarta genom att fokusera 
på fem pusselbitar. 

Dessa är inte slumpmässigt valda, de har formats genom år av utforskande, 
reflektion och samtal med människor som precis som du vill leva med mer 
närvaro, riktning och glädje.

 ## *De fem pusselbitarna i livet är*
 JAG – Den du är, din hälsa, medvetenhet, drömmar, balans och personliga 
utveckling.

FAMILJ OCH RELATIONER – Banden som formar dig, både biologiska 
och icke-biologiska. Dessa relationer bär dig i såväl glädje som utmaning.
 
GEMENSKAP – Ditt större sammanhang. Vänner, nätverk och de miljöer 
där du känner tillhörighet.

SYSSELSÄTTNING – Hur du använder din energi varje dag: arbete, passioner, ansvar eller projekt som ger struktur och mening.

GE TILLBAKA (OMTANKE) – Hur du bidrar till något större genom tid, 
omtanke, resurser eller engagemang. 

Det finns inget rätt eller fel sätt att definiera dessa områden. Det som spelar 
roll är att du gör det inifrån dig själv. Vad betyder dessa delar för dig? Vilken 
känsla vill du odla i varje del av ditt liv?
 ## *Definiera varje område utifrån dig själv*
Börja med att skriva ner eller använda exemplen ovan för att se vad varje 
pussel bit betyder för dig personligen. Vad vill du att de ska stå för i ditt liv? 
Här finns inga mallar, bara din egen sanning.`;

const questionsStep2 = [
    {
        "body": [
            `:::dropdown[Se exempel]
JAG – Den du är, din hälsa, medvetenhet, drömmar, balans och personliga 
utveckling.

FAMILJ OCH RELATIONER – Banden som formar dig, både biologiska 
och icke-biologiska. Dessa relationer bär dig i såväl glädje som utmaning.
 
GEMENSKAP – Ditt större sammanhang. Vänner, nätverk och de miljöer 
där du känner tillhörighet.

SYSSELSÄTTNING – Hur du använder din energi varje dag: arbete, passioner, ansvar eller projekt som ger struktur och mening.

GE TILLBAKA (OMTANKE) – Hur du bidrar till något större genom tid, 
omtanke, resurser eller engagemang.`,
        ]
    },
    {
        "title": "Börja med att skriva ner eller använda exemplen ovan för att se vad varje pusselbit betyder för dig personligen.",
        "body": "**JAG**",
        "hasTextBox": true,
    },
    {
        "body": "**FAMILJ OCH RELATIONER**",
        "hasTextBox": true,
    },
    {
        "body": "**GEMENSKAP**",
        "hasTextBox": true,
    },
    {
        "body": "**SYSSELSÄTTNING**",
        "hasTextBox": true,
    },
    {
        "body": "**GE TILLBAKA (OMTANKE)**",
        "hasTextBox": true,
    }
];

const textStep3 = `
## *Steg 3 av 9 Reflektera med vägledande frågor i varje område*
 *Att skapa ett liv med djup handlar inte om att börja från noll. Det 
handlar om att återvända till det som redan är sant – din essens –  och bygga vidare därifrån.*

 När du har definierat vad varje område innebär för dig, har du gjort något 
stort. Du har börjat formulera din egen karta, inte baserad på andras förväntningar, utan på din egen sanning. Det är precis här det blir intressant för nu 
kliver du in djupare. Du får chansen att stanna upp, lyssna in och känna: 
*Hur mår varje område i mitt liv just nu? Hur känns det i vardagen? Vad fungerar? 
Vad skaver? Vad längtar jag efter?*

 Du börjar bygga din egen berättelse, inte med perfekta svar, utan med 
närvaro och ärlighet. Varje gång du sätter ord på något, varje gång du vågar se 
klart, händer något. Du kliver ur autopiloten och in i ägarskap.

 Att veta att du alltid har förmågan att välja närvaro framför flykt och ansvar 
framför undvikande, oavsett var du kommer ifrån, vad du varit med om eller 
vilka omständigheter du har just nu, är inte bara klokt – det är kraftfullt. 
Det är modigt och ja, det är faktiskt rätt coolt.
 
Genom att medvetet fokusera på dessa områden skapar du grunden för ett 
balanserat, meningsfullt och syftesdrivet liv. 
Låt oss dyka ner och börja skapa ditt personliga mästerverk!
`;

const textStep4 = `
## *Steg 4 av 9 Zooma ut och se mönstret*
*Stanna upp för att se dina mönster – vad visar helheten?*
 Nu är det dags att stanna upp en stund för att kalibrera. Du har samlat värdefull information från varje område i ditt liv, nu får du chansen att backa ett 
steg, lyfta blicken och börja se det större mönstret.
 ## *Vad avslöjar hela pusslet, din livskarta?*
 Att se mönster betyder att börja lägga märke till det som återkommer: hur du 
känner, agerar, väljer, eller undviker, i flera delar av livet. Det handlar inte om 
att leta fel, utan om att få syn på din nuvarande inre berättelse. Vad driver dig? 
Vad styr dig? Vad håller dig tillbaka?

 *Kanske* märker du att du har svarat olika på varje område, att inget 
riktigt hänger ihop. Det är också ett mönster: kanske saknas en tydlig 
riktning just nu.

 *Kanske* ser du att samma behov eller längtan dyker upp om och om 
igen, som närvaro, balans, ärlighet eller energi.

 *Kanske* märker du ett mönster av undvikande, att du glider undan 
känslor eller inte riktigt sätter ord på det du innerst inne vet.

 Det här steget handlar om frihet, men inte frihet som i att fly, utan frihet som 
i att se klart. Och just därför är det så viktigt att *pausa, zooma ut och sedan gå 
tillbaka in igen*. När du tränar på att växla mellan överblick och närvaro, mellan helhet och detalj, börjar du bygga en mental och emotionell styrka som bär 
dig, oavsett vad som händer. Du fastnar inte i detaljer eller i tillfälliga känslor. Du ser sammanhang och dig själv tydligare. Det är så du behåller friheten i 
tanken, känslan och agerandet. Du rör dig inte längre reaktivt genom livet 
utan skapar medvetet. Du lär dig läsa kartan medan du ritar den.

 Detta är inte bara ett reflektionssteg, det är en livsfärdighet att kunna stanna 
upp, kliva tillbaka, se helheten och sedan fortsätta, grundad i det du sett.
`;

const questionsStep4 = [
    {
        "title": "Ditt mönster just nu",
        "body": "Vilka teman, behov eller känslor återkommer i flera områden? Vad mär ker du när du tittar på helheten? Skriv ner ditt svar men håll det kort, gärna bara någon mening. Det hjälper dig att behöva vara tydlig. Vilket mönster visar sig?",
        "hasTextBox": true,
    },
    {
        "body": "Vad säger mönstret om vad du längtar efter, behöver eller är redo att förändra?",
        "hasTextBox": true,
    },
    {
        "body": "När du har sett dina mönster och fått syn på helheten är nästa steg att komma nära nuet. För att kunna göra medvetna val behöver du veta var du står, här och nu. Nu är det dags för din nulägeskattning, en ärlig avstämning med varje livsområde, så att du kan ta nästa steg med klarhet och riktning.",
    }
];

const textStep5 = `
##  *Steg 5 av 9 Skatta nuläget från 0 till 10*
 Innan du går vidare är det återigen dags att stanna upp. Inte för att döma, fixa 
eller analysera sönder något, utan för att ge dig den gåva det är att möta dig 
själv med ärlighet och medkänsla.

 Detta är din nulägesskattning, en tydlig och närvarande avstämning med 
var du befinner dig just nu i livet. Det handlar om att se vad som fungerar, 
vad som saknas och var din energi behövs mest just nu. I stället för att rusa 
vidare framåt får du här en chans att landa, känna efter och ta nästa steg med 
förankrad avsikt.

 ## *Använd en skala från 0 till 10 för att skatta din upplevelse inom varje pusselbit*
 När du skattar varje område, sikta mot det vi kallar för en tröskel för väl
mående, ofta runt 7 eller högre. Varför 7? För att det är där du inte bara klarar 
dig utan rör dig framåt. Du bär dig själv genom vardagen med energi, närvaro 
och uthållighet.

 10 betyder att du känner dig helt i linje, trygg, energifylld och i  
balans i det området. 0 signalerar att något är ur kurs, ett område som ropar 
efter omsorg och uppmärksamhet. 

## Tänk dig ...
### att din sysselsättning landar på en fyra. Du märker att detta område ger trygghet men lite energi. En längtan efter mer kreativitet eller mening börjar göra sig hörd, något inom dig vill vakna. Samtidigt kanske du skattar gemenskap till en åtta. Du känner dig nära dina vänner, har samtal som bär och upplever att det här området faktiskt ger dig energi. Det är viktigt att se båda delarna.
`;

const questionsStep5: TaskContent = [
    {
        "title": "*Använd en skala från 0 till 10 för att skatta din upplevelse inom varje pusselbit*",
    },
    // TODO: Change these to use number inputs instead of generic text boxes
    {
        "body": "*Jag*",
        "hasTextBox": true,
        lines: 1,
    },
    {
        "body": "*Familj*",
        "hasTextBox": true,
        lines: 1,
    },
    {
        "body": "*Gemenskap*",
        "hasTextBox": true,
        lines: 1,
    },
    {
        "body": "*Sysselsättning*",
        "hasTextBox": true,
        lines: 1,
    },
    {
        "body": "*Ge tillbaka*",
        "hasTextBox": true,
        lines: 1,
    },
    {
        "body": [
            ` De pusselbitar som hamnar under tröskeln behöver inte ses som problem, 
utan som inbjudningar. Inbjudningar till att ta hand om det som skaver, att 
återknyta kontakten med det som är viktigt.`,
            `Genom att ge dessa delar uppmärksamhet börjar du skifta från att överleva 
till att leva mer i balans och sanning.`,
            ` De områden som redan mår bra är värdefulla att förstärka. De ger dig stabilitet. Ju mer de bär, desto friare kan du röra dig, både inåt och utåt.
 Den här skattningen handlar inte bara om att få höga siffror, den handlar 
om att veta var du faktiskt är, just nu. Att möta dig själv med klokhet, öppenhet och respekt. För när du ser det tydligt, kan du också agera tydligt från en 
plats av sanning. Det här är ditt ögonblick att förankra din vision i verkligheten och därifrån, med fötterna på jorden, designa ditt nästa steg.`,
            ` När du nu har fått syn på var du står blir nästa steg naturligt: *Hur vill du 
att det ska vara?* Att ta reda på vad du vill handlar inte om att ha en färdig plan 
utan om att våga känna skillnaden mellan nuläget och den riktning som känns 
mer sann, mer levande, mer du.`,
            ` I nästa steg får du chansen att börja formulera den riktningen. Genom att 
spegla varje område mot din längtan skapas klarhet. Hur vill du känna dig? 
Vad skulle vara annorlunda om du levde mer i linje med det som betyder 
något för dig?`,
            ` Det här är inte stora livsbeslut, det är små, ärliga svar som pekar mot nästa 
steg. Ditt steg.`
        ]
    }
];

const textStep6 = `
## *Steg 6 av 9 Formulera din riktning*
 ## *Från nuläge till önskat läge – att börja välja medvetet*
 Det här steget hjälper dig att ta klivet från autopilot till närvaro. Från att reagera på livet till att faktiskt välja det. Här tränas du i ett förhållningssätt, en 
inre orientering som gör det möjligt för dig att röra dig mellan djup och perspektiv. Du använder det som är sant just nu, men låter dig samtidigt öppnas 
mot det som är möjligt. Det handlar inte om att lämna verkligheten, utan om 
att möta den och välja att inte fastna i gamla idéer om hur saker måste vara. 
Du övar dig i att lyssna djupare, att se ärligt och att välja med hjärta.

 Men låt det här vara mer än en tankeövning. Ta med hela dig in i processen. Andas, mjukna, rör dig om det behövs. Sätt på musik, dansa, gå en stund i tystnad. Gör vad du behöver för att komma närmare dina känslor. Träna på att 
fånga det som dyker upp först, innan huvudet börjar överanalysera det. Lita på 
att du vet mer än du tror, när du lyssnar inifrån. 

Det här är inte tungt eller svårt, det är ett sätt att ta livet i handen. Med 
kärlek, med skratt, med disciplinen att ta ansvar för din potential och lysa i 
dina färger.

 Nu är det dags att ta det du såg i dina mönster i steg 4 och det du upptäckte 
i nulägesskattningen i steg 5 och börja formulera vilken riktning du faktiskt 
vill gå i. Fokus behöver inte alltid vara att försöka laga det som inte fungerar, 
ibland handlar det om att kliva in i det som är redo att växa.
`;

const questionsStep6: TaskContent = [
    {
        "title": "*Titta på varje pusselbit i din livskarta och ställ följande frågor*",
        body: [`Hur känns det här området just nu?

 Vad skulle jag i stället vilja känna, uppleva eller förverkliga?

 Vilken förändring, längtan eller kvalitet vill jag börja ge utrymme för?

 Svara enkelt. Det räcker med ett ord eller uttryck. Lita på det som kommer. 
För dig som är van vid många ord, här är din chans att prova något nytt. Det 
är inte bättre. Inte sämre. Bara ett annat sätt.
`,
            `
:::dropdown[Exempel på förflyttning från nuläge till önskat läge]
 ### Jag 
Splittrad – fokuserad

Trött – energifylld

Stillastående – skapande

Okej – levande

### Familj och relationer
 Distanserad – närvarande

 Reaktiv – medveten

 Praktisk – innerlig

 Vardagsrutiner – gemenskap med glöd

 ### Sysselsättning
 Tom – meningsfull

 Stabil – inspirerande

 Energikrävande – energigivande

 Måste – vill

 ### Sammanhang/gemenskap
 Ensam – delaktig

 Känna mig exkluderad – känna mig inkluderad

 Social yta – äkta kontakt

 Observerande – medskapande

 ### Ge tillbaka/omtanke
 Otydlig – engagerad

 Längtande – agerande

 Gömd röst – synlig påverkan

 Vilja – handling`,
        ]
    },
    {
        "body": "### Jag",
        hasTextBox: true,
        lines: 1,
    },
    {
        "body": "### Familj och relationer",
        hasTextBox: true,
        lines: 1,
    },
    {
        "body": "### Sysselsättning",
        hasTextBox: true,
        lines: 1,
    },
    {
        "body": "### Sammanhang/gemenskap",
        hasTextBox: true,
        lines: 1,
    },
    {
        "body": "### Ge tillbaka/omtanke",
        hasTextBox: true,
        lines: 1,
    },
    {
        "body": `Det här steget handlar inte om rätt svar, det handlar om riktning, om att börja 
röra dig framåt. Inte mot något du borde vara, utan mot något som redan lever 
i dig men som nu får ta lite mer plats. När du säger det högt eller när du skriver ner det, börjar något röra sig. Du får kontakt med det som är sant för dig. Nu 
börjar din karta få sin form och riktning, en som du valt medvetet och öppet.  `
    }
];

const textStep7 = `
## *Steg 7 av 9 Identifiera gapet mellan nuläge och önskat läge*
 Nu är det dags att stanna upp och rikta blicken mot mellanrummet, gapet 
mellan där du är och där du vill vara.

 Det här utrymmet, som ibland kan kännas tomt eller obekvämt, är i själva 
verket fullt av information. Här bor insikten, riktningen och potentialen. Det 
är inte ett tomrum, det är ett möjlighetsfält.

 Att se på detta gap med ärlighet och nyfikenhet är en färdighet. Det handlar inte bara om att fokusera på vad som saknas, utan om att upptäcka vad som 
är möjligt. Det är här den yttre strukturen får möta det inre landskapet. När vi 
tydligt identifierar vad som behöver stärkas, riktning, mod eller vanor, öppnar 
vi också upp för att våra känslor, tankar och upplevelser får ta plats på riktigt. 
Vi börjar se verkligheten, inte som ett hinder, utan som en grund att bygga 
vidare från. Men ibland vill vi inte se gapet. Vi förskönar, förvränger eller tittar 
bort. För vi vet att det är ett val och att välja är att ta ansvar. Men just där, i 
den platsen, bor också friheten. 

När vi accepterar verkligheten som den är, utan att döma, och samtidigt 
inser att vi kan påverka och skapa, återtar vi vår kraft. Det är inte alltid den 
enklaste vägen, men det är den sanna. 

Gapet blir då inte ett hinder, utan en spegel. En plats att kliva in i med 
klarhet, medvetenhet och framåtrörelse, inte från brist, utan från styrka. Det är ett sätt att leva med integritet, ansvar och mod. Transformationen har redan 
börjat och steg för steg tar du ägarskap över ditt liv och din livsstil.

 På nästa sida och framåt finns inspiration på vad ett gap kan vara inom varje 
pusselbit, men kom ihåg att också känna det. Låt kroppen svara. Låt känslorna 
viska, och låt det vara okej att inte veta exakt. Gapet är inte bara något du ska 
förstå, det är något du behöver uppleva. Du bär redan svaren inom dig. Ingen 
annan vet mer om dig än du själv, när du stannar upp och lyssnar.

 Du äger dina egna gap och du har alltid möjligheten att ombestämma dig. 
Livet är inte statiskt, det är levande, och du är en del av det. När du följer med 
i din naturliga utveckling, i din inre rytm snarare än gamla berättelser, börjar 
ett liv ta form som är sant, närvarande och fritt.
 ## *Skriv ner det som lever i mellanrummen*
Kom ihåg att det också kan finnas delar där det inte finns något gap. Så tvinga 
inte fram något. Slappna av. Det självklara visar sig när vi släpper taget och 
sjunker in i vår kärna. Det blir så mycket lättare när vi är avslappnade, mjuka 
och nyfikna.
`;

const questionsStep7: TaskContent = [
    {
        title: "## *Möt ditt gap med ärlighet och mjukhet*",
        body: "Ett gap är inte ett fel eller en brist, det är en portal till nästa nivå av dig själv. "
    },
    {
        body: [
            "Exempel på gap, från nuläge till önskat läge:",
            `:::dropdown[Jag]
 *Splittrad – fokuserad*

 Detta gap kan handla om att samla uppmärksamheten och skapa 
struktur i vardagen. Något som kräver tydlighet, gränssättning och 
närvaro.

 *Trött –energifylld*

 Här kan gapet ligga i återhämtning, sömn, rörelse och val som ger 
energi snarare än dränerar. Det kräver omtanke och prioritering.

 *Stillastående – skapande*

 Gapet kan bestå av modet att börja, att släppa prestation och låta 
kreativiteten få ta plats utan krav. Det handlar om att agera trots 
tvekan.

 *Okej – levande*

 Här kan gapet handla om att våga känna mer, glädje, sorg, längtan. 
Att inte nöja sig med det som är neutralt, utan söka det som 
väcker livslust.`
        ],
        hasTextBox: true,
    },
    {
        body: [
            `:::dropdown[Familj och relationer]
 *Distanserad – närvarande*

 Gapet kan handla om tid, lyssnande och att faktiskt vara där, mentalt, 
känslomässigt och fysiskt, vilket kräver öppenhet och närvaro.

 *Reaktiv – medveten*

 Här kan gapet ligga i pausen, att hinna andas innan du svarar. Att 
välja empati i stället för försvar. Att våga möta utan att lösa.

 *Praktisk – innerlig*

 Detta gap kan handla om att se förbi rutinerna och skapa små, 
menings fulla ögonblick av kontakt. En blick, ett samtal, en beröring.

 *Vardagsrutiner – gemenskap med glöd*

 Det här gapet kan kräva intention. Att skapa stunder där ni möts 
och inte bara gör. Det handlar om att bjuda in värme och lekfullhet 
i vardagen.
`
        ],
        hasTextBox: true,
    },
    {
        body: [
            `:::dropdown[Sysselsättning]
 *Tom – meningsfull*

 Gapet kan ligga i att utforska vad som ger dig syfte. Att våga justera 
riktning, se ditt varför, och skapa en känsla av att bidra.

 *Rigid – inspirerande*

 Här kan gapet handla om expansion. Vad vill du utveckla? Lära dig? 
Utforska? Det kräver nyfikenhet och mod att kliva ur komfortzonen.

 *Energikrävande – energigivande*

 Gapet kan här ofta bestå av gränssättning, att säga nej till det som dränerar och ja till det som ger. Det handlar om balans och självrespekt.

 *Måste – vill*

 Det här gapet kan kräva ett nytt perspektiv. Kan du omforma uppgiften? Eller är det dags att förändra riktning helt?`
        ],
        hasTextBox: true,
    },
    {
        body: [
            `:::dropdown[Sammanhang/gemenskap]
 *Ensam – delaktig*

 Gapet kan handla om att bjuda in och bjuda på dig själv. Att ta initiativ, visa dig och våga möta andra där de är.

 *Känna mig exkluderad – känna mig inkluderad*

 Här kan gapet handla om att våga kliva in. Att kommunicera dina 
behov, men också ge andra en chans att möta dig.

 *Ytlig – äkta kontakt*

 Gapet kan ligga i modet att vara sårbar. Att ställa frågor som betyder 
något, att lyssna bortom orden.

 *Observerande – medskapande*

 Här kan gapet handla om att ta plats. Att släppa rädslan för att störa, 
och i stället bidra med det du har, oavsett om det är tystnad, energi 
eller idéer.
`
        ],
        hasTextBox: true,
    },
    {
        body: [
            `:::dropdown[Ge tillbaka/omtanke]
 *Otydlig – engagerad*

 Gapet kan vara att du lyssnar inåt. Vad vill du bidra med och hur? 
Det handlar om att förtydliga din riktning och börja agera.

 *Längtande – från idé till verklighet*

 Här kan gapet ligga i steget från tanke till handling. Att inte vänta på 
rätt tillfälle utan skapa det.

 *Gömd röst – synlig påverkan*

 Gapet kan handla om att tala även när rösten darrar. Att våga stå för 
det du tror på och visa vad du bär inom dig.

 *Vilja – handling*

 Det här gapet kan kräva mod. Att inte bara känna, utan att göra. Att 
låta dina värderingar bli synliga i ditt agerande.
`
        ],
        hasTextBox: true,
    }
];

const textStep8 = `
## *Steg 8 av 9 Zooma ut och upptäck den gemensamma nämnaren*
När du har kartlagt dina fem gap mellan där du är och där du vill vara i varje 
livsområde, har du skapat något värdefullt. Du ser inte bara varje pusselbit 
för sig, utan har nu också chansen att lyfta blicken och upptäcka sambanden.
## *Finns det ett tema som återkommer i flera områden?*
Finns det något som skulle kunna skapa förändring på flera nivåer samtidigt 
om det fick mer utrymme? En särskild känsla, ett behov eller en riktning?
Det här är din möjlighet att möta det vid roten där även den minsta hand
ling kan sprida sig utåt och förändra flera dimensioner av ditt liv.

## *Dimensioner av ditt liv*
*Sårbarhet* – att våga visa mer av dig själv, även när det känns ovant. 

*Mod* – att våga gå utanför det bekväma, tala din sanning, agera 
trots obehag. 

*Prioritering* – att sluta splittra din energi och börja välja vad som 
verkligen är viktigt.

*Kontakt* – att öppna upp för gemenskap, intimitet och verklig närhet.

*Självrespekt* – att stå upp för dig själv, sätta gränser och hedra dina 
behov.

*Glädje* – att släppa lite kontroll, leka mer, bjuda in lätthet och skratt 
i vardagen.

Ibland dyker ett tydligt ord upp, ibland flera. Låt det vara så. Det finns inget 
rätt eller fel här. Men om du står mellan flera, välj det som just nu väcker mest känsla, nyfikenhet eller längtan. Det blir ditt fokusord, en slags essens som 
hjälper dig att hålla riktningen framåt.

Det handlar inte om att fixa allt. Det handlar om att börja där det bränner 
till, där något vill ta mer plats. Det ordet blir din vägvisare för nästa steg. 
`;

const questionsStep8: TaskContent = [
    {
        body: "## *Skriv ner dina gap på de fem raderna nedan*",
    },
    {
        hasTextBox: true,
        lines: 1,
    },
    {
        hasTextBox: true,
        lines: 1,
    },
    {
        hasTextBox: true,
        lines: 1,
    },
    {
        hasTextBox: true,
        lines: 1,
    },
    {
        hasTextBox: true,
        lines: 1,
    },
    {
        body: "## *Skriv ner det fokusord som förenar gapen ovan*",
        hasTextBox: true,
        lines: 1,
    }
];

const textStep9 = `
## *Steg 9 av 9 Samla insikterna och skapa en levande struktur*
## *Det här är början på resan där du börjar leva din livsstil*
Nu är det dags att samla ihop allt du har sett, känt och förstått och börja 
omsätta det i rörelse. Din karta är inte bara en samling reflektioner. Den är 
grunden till ett liv du faktiskt vill leva. Det sista steget handlar om att fokusera och sedan agera. Titta på din karta och se vilket område som just nu ropar 
högst efter din uppmärksamhet. Använd din skattning från nuläget och lyssna 
in ditt fokusord. Låt det bli din startpunkt. Kom ihåg, förändring sker inte av 
en slump, du har arbetat dig fram steg för steg till det du nu ska börja testa. 

Du har identifierat vad som kommer att göra störst skillnad och varför det 
spelar roll. För att det ska bli på riktigt, hållbart, levande och sant, behöver du 
något mer: Disciplin, dedikation och uthållighet, en helhetsförankring.
## *En kärleksfull handling*
Disciplin är en kärleksfull handling gentemot dig själv, en daglig påminnelse 
om att återvända till det som verkligen betyder något, även när det känns 
motigt eller tråkigt. Den bor i det mentala: i ditt fokus, din riktning och förmågan att hålla dig på banan även när distraktionerna lockar.

Det finns inget tvång i disciplin, bara viljan att hålla löften till dig själv. Inte genom hårdhet, utan med kärleksfull konsekvens.
## *Dedikation är din inre glöd*
Den bärs av det emotionella, av hjärtats längtan och viljan att vara sann mot 
det du älskar. Det är känslan av Jag gör det här för att det betyder något. Dedikation gör att du stannar kvar, även när det går långsamt. Det är den inre lågan som håller din riktning levande.
## *Uthållighet lever i kroppen*
Uthållighet formas genom det fysiska, det jordade, det konkreta. Den är tilliten 
till att små steg räknas, och styrkan att fortsätta även när du inte ser resultat 
direkt. Du behöver inte ha bråttom, bara vara villig att ta nästa steg. Uthållighet är den tysta kraften som hjälper dig att bära det du bygger hela vägen fram.

Tillsammans bildar disciplin, dedikation och uthållighet en helhet i balans, 
ett sätt att leva där sinne, hjärta och kropp samverkar med din riktning. Det är 
så en livsstil formas, inte bara för att fungera, utan för att kännas meningsfull 
och fri. Du är inte färdig. Du är i rörelse. Och det är precis där du ska vara.

## *Summering av niostegsprocessen – din livskarta*

De nio stegen du just har gått igenom är mer än en övning, de är början på att 
skapa ett liv som känns levande. Livskartan är inte något du gör en gång och 
sedan lämnar. Det är ett återkommande arbete. En struktur att återvända till. 
Med tiden blir det ett naturligt sätt att navigera, inte bara i tydliga val utan i 
hela ditt sätt att förhålla dig till livet.

Din karta hjälper dig att leva nära dina värderingar i riktning mot ditt syfte 
och din Nordstjärna. Den påminner dig om att det är du som håller i kom
passen, att det liv du längtar efter inte är något som finns långt borta, utan är 
något du bygger, steg för steg, varje dag. Ibland kommer livet att överraska. 
Du kommer att möta sol, storm, tystnad, rörelse. Men du kommer också att 
ha med dig något ovärderligt: en karta, och en garderob med kläder för varje 
väder. När något skaver, när du tappar kursen, vet du hur du kan stanna upp, 
lyssna in, justera och fortsätta. Med närvaro, riktning och stöd.

En gång i månaden kan du återbesöka kartan, gå igenom stegen igen och 
se vad som har förändrats. Mellan gångerna lever du experimentellt, testar, 
reflekterar, justerar.

Detta är början på en livsstil där du inte bara reagerar på livet, utan där du 
möter det, äger det och formar det inifrån. Och när dagen kommer då du lämnar den här jorden vet du att du levt sant. Du har gjort skillnad. Ditt avtryck 
är unikt – precis som det var menat att vara.
`;

const questionsStep9: TaskContent = [
    {
        body: `
## *Summering av niostegsprocessen – din livskarta*
Hur känns det nu när du har tagit dig igenom de nio stegen, och vad har du upptäckt som varit din främsta utmaning längs vägen?
`,
        hasTextBox: true,
    },
    {
        imagePath: "/img/KORRIGERAD_stig_med_karta_och_kompass.png",
    }
];

type StepInfo = {
    key: string,
    readButtonText: React.ReactNode,
    readContent: string,
    taskElement?: ({ initialState, onStateChanged }: { initialState: { completed?: boolean }, onStateChanged: (newState: typeof initialState) => void }) => JSX.Element,
    taskButtonText?: React.ReactNode,
    taskContent?: TaskContent
};
const steps: StepInfo[] = [
    {
        key: "1a",
        readButtonText: "Läs steg 1a",
        readContent: textStep1a,
        taskElement: TimelineTaskDialog,
    },
    {
        key: "1b",
        readButtonText: "Läs steg 1b",
        readContent: textStep1a,
        taskContent: questionsStep1b,
        taskButtonText: "Reflektion",
    },
    {
        key: "2",
        readButtonText: "Läs steg 2",
        readContent: textStep2,
        taskContent: questionsStep2,
        taskButtonText: "De fem pusselbitarna",
    },
    {
        key: "3",
        readButtonText: "Läs steg 3",
        readContent: textStep3,
        taskElement: LifeMapDialog,
    },
    {
        key: "4",
        readButtonText: "Läs steg 4",
        readContent: textStep4,
        taskContent: questionsStep4,
        taskButtonText: "Reflektion",
    },
    {
        key: "5",
        readButtonText: "Läs steg 5",
        readContent: textStep5,
        taskContent: questionsStep5,
        taskButtonText: "Skatta nuläget",
    },
    {
        key: "6",
        readButtonText: "Läs steg 6",
        readContent: textStep6,
        taskContent: questionsStep6,
        taskButtonText: "Formulera din riktning",
    },
    {
        key: "7",
        readButtonText: "Läs steg 7",
        readContent: textStep7,
        taskContent: questionsStep7,
        taskButtonText: "Identifiera gapet",
    },
    {
        key: "8",
        readButtonText: "Läs steg 8",
        readContent: textStep8,
        taskContent: questionsStep8,
        taskButtonText: <>Den gemensamma<br />nämnaren</>,
    },
    {
        key: "9",
        readButtonText: "Läs steg 9",
        readContent: textStep9,
        taskContent: questionsStep9,
        taskButtonText: "Summering",
    },
];

type NineStepProcessTaskProps = {
    taskId: TaskId;
    // onUnsavedChanged: (unsaved: boolean) => void;
    onSave?: (completed: boolean) => (void | Promise<void>);
    // onSaved: () => void;
};

// To simplify the data state, all of these should probably just be plain strings here
type NineStepProcessData = {
    "0"?: Values,
    "1a"?: TimelineData,
    "1b"?: Values,
    "2"?: Values,
    "3"?: LifeMapData,
    "4"?: Values,
    "5"?: Values,
    "6"?: Values,
    "7"?: Values,
    "8"?: Values,
    "9"?: Values,
};

// This shouldn't be hardcoded
const PART_COUNT = 11;

export function NineStepProcessTask({ taskId, onSave }: NineStepProcessTaskProps) {
    const { data: userData, task, save, dataStatus, } = useTask({ taskId: taskId });

    const [data, setData] = useState<NineStepProcessData>({});// This needs to be a state instead
    const [openDialogs, setOpenDialogs] = useState<{ [key: string]: boolean }>({});

    // TODO: Store the task parts under different ids instead of storing
    // everything inside of index 0
    useEffect(() => {
        if (!userData?.data?.[0] || dataStatus !== "success") return;
        setData(JSON.parse(userData.data["0"]));
    }, [dataStatus, userData?.data]);

    const handleTaskSave = async (values: unknown, key: string) => {
        const newState = { ...data, [key]: values };
        const completed = checkCompleted(newState);

        setData(newState);
        onSave?.(completed);
        save({
            data: {
                data: {
                    0: JSON.stringify(newState)
                },
                version: 2
            },
            completed: completed
        });
    };

    const checkCompleted = (data: NineStepProcessData) => {
        const values = Object.values(data);

        // Must have done all parts
        if (values.length !== PART_COUNT) return false;

        // All parts must be completed
        return values.every((values) => {
            return Object.keys(values).includes("completed")
                ? (values as { completed?: boolean }).completed
                : Object.values(values).every(v => v && v !== "");
        });
    };
    return (
        <>
            <div className="absolute top-2 left-2">
                <CloudStatusIcon status={dataStatus} />
            </div>
            <TaskContentElement
                title="Lära: :section-title"
                values={Object.assign({ ...data["0"] })}
                onValuesChanged={(values) => {
                    handleTaskSave(values, "0");
                }}
                content={[{

                    body: [
                        "**Syfte:** Att bli mer ärlig, mer närvarande och mer medveten i det liv du redan lever.",
                        "",
                        "**Vad vill du att en design ska ge för resultat?**",
                        "Vad tror du kan bli din utmaning när du steg för steg avgränsar och formar det liv du vill leva?"
                    ],
                    hasTextBox: true,
                    lines: 2
                }]}
            />
            <div className="grid gap-2 grid-cols-[1fr_2fr]">
                {steps.map((info) => {
                    return <StepRow
                        key={info.key}
                        textButtonText={info.readButtonText}
                        text={info.readContent}
                        rightElement={
                            info.taskElement
                                ? info.taskElement({
                                    initialState: data[info.key],
                                    onStateChanged: (state: unknown) => {
                                        handleTaskSave(state, info.key);
                                    }
                                })
                                : <Dialog
                                    open={openDialogs[info.key]}
                                    onOpenChange={(open) => setOpenDialogs(prev => ({ ...prev, [info.key]: open }))}
                                    trigger={
                                        <Button
                                            variant={(Object.values(data[info.key] ?? { undefined: undefined }).every(v => Boolean(v))) ? "success" : "step-current"}
                                        >
                                            {info.taskButtonText}
                                        </Button>
                                    }
                                >
                                    <div className="absolute top-2 left-2">
                                        <CloudStatusIcon status={dataStatus} />
                                    </div>
                                    <TaskForm
                                        content={info.taskContent}
                                        defaultValues={data[info.key]}
                                        onSave={(values) => handleTaskSave(values, info.key)}
                                        allowEmptyAnswers={true}
                                        onSaved={undefined}
                                        onUnsavedChanged={undefined}
                                        onValuesChanged={(values) => {
                                            handleTaskSave(values, info.key);
                                        }}
                                    />
                                    <div className="flex flex-row justify-end w-full pr-2">
                                        <Button onClick={() => {
                                            setOpenDialogs(prev => ({ ...prev, [info.key]: false }));
                                        }}>
                                            Stäng
                                        </Button>
                                    </div>
                                </Dialog>
                        }
                    />;
                })}
            </div>
        </>
    );
}

function StepRow({ textButtonText, text = "", rightElement }) {
    return <>
        <ReadDialog
            text={text}
            trigger={
                <Button variant="step-current">
                    {textButtonText}
                </Button>
            }
        />
        {rightElement}
    </>;
}